import { useEffect, useMemo, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import localLawService from "api/localLawService";
import {
  EPublicationRequestType,
  IPublicationRequest,
} from "dto/LocalLaw/IPublication";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface Props {
  onClose: () => void;
  onCreated: (request: IPublicationRequest) => void;
}

// Kreator wniosku o ogłoszenie aktów w wojewódzkim dzienniku urzędowym.
// Port UPE.Forms z Magistratu — z jedną różnicą: tam pierwszym krokiem było logowanie
// do UPE (AuthorizationPage), a u nas dane logowania są integracją profilu, więc
// kreator zaczyna od rodzaju wniosku.
//
// Kroki odpowiadają stronom kreatora: RequestTypePage → RequestInfoPage →
// RequestContentPage → ReadyToSendPage. Podpis i wysyłka dzieją się później, na liście
// wniosków: paczkę podpisuje wtyczka na stanowisku, a wysyła backend.

const TYPES: { value: EPublicationRequestType; label: string; hint: string }[] = [
  {
    value: EPublicationRequestType.Lapx,
    label: "Ogłoszenie aktu w dzienniku urzędowym",
    hint: "Podstawowy wniosek — akty przekazywane są jako paczki ZIPX z XML aktu.",
  },
  {
    value: EPublicationRequestType.Withdraw,
    label: "Wycofanie aktu z ogłoszenia",
    hint: "Wniosek o wycofanie aktu przekazanego wcześniej do dziennika.",
  },
  {
    value: EPublicationRequestType.LapxRio,
    label: "Przekazanie do nadzoru RIO",
    hint: "Akty objęte nadzorem Regionalnej Izby Obrachunkowej.",
  },
  {
    value: EPublicationRequestType.LapxRioProtocol,
    label: "Dokumenty do procesu nadzoru RIO",
    hint: "Pismo z dodatkowymi dokumentami (np. wyciąg z protokołu sesji).",
  },
];

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const PublicationWizard = ({ onClose, onCreated }: Props) => {
  const [step, setStep] = useState(0);

  const contextQuery = localLawService.useGetPublicationContext(true);
  const { mutate: create, isLoading: isCreating } =
    localLawService.useCreatePublicationRequest();

  const context = contextQuery.data?.data;

  const [type, setType] = useState(EPublicationRequestType.Lapx);
  const [signature, setSignature] = useState("");
  const [requestDate, setRequestDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [recipientId, setRecipientId] = useState<number | undefined>(undefined);
  const [rioId, setRioId] = useState<number | undefined>(undefined);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [position, setPosition] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  // Domyślny adresat to redakcja dziennika wskazana przez UPE — tak samo jak
  // RequestInfoViewModel brał Proxy.GetDefaultReceiver().
  useEffect(() => {
    if (recipientId !== undefined) return;

    const preferred = context?.Receivers.find((x) => x.IsDefault);

    if (preferred) setRecipientId(preferred.Id);
  }, [context, recipientId]);

  useEffect(() => {
    if (!context?.EmployeeName || firstName || lastName) return;

    const parts = context.EmployeeName.split(" ").filter(Boolean);

    setFirstName(parts.slice(0, -1).join(" ") || parts[0] || "");
    setLastName(parts.length > 1 ? parts[parts.length - 1] : "");
  }, [context, firstName, lastName]);

  const isRio =
    type === EPublicationRequestType.LapxRio ||
    type === EPublicationRequestType.LapxRioProtocol;

  const blocked = !context?.Account.Configured || !!context?.Warning;

  const acts = useMemo(() => context?.Acts ?? [], [context]);

  const selectedActs = useMemo(
    () => acts.filter((a) => selected.includes(a.LegalActId)),
    [acts, selected],
  );

  const canGoNext = () => {
    if (blocked) return false;
    if (step === 1) return !!signature.trim() && !!requestDate && !!recipientId;
    if (step === 2) return selected.length > 0;
    return true;
  };

  const toggleAct = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  const submit = () => {
    create(
      {
        Type: type,
        Signature: signature.trim(),
        RequestDate: requestDate,
        RecipientInstitutionId: recipientId,
        RioInstitutionId: isRio ? rioId ?? recipientId : undefined,
        LegalActIds: selected,
        EmployeeFirstName: firstName.trim() || undefined,
        EmployeeLastName: lastName.trim() || undefined,
        EmployeePosition: position.trim() || undefined,
      },
      {
        onSuccess: (response) => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Wniosek złożony",
            text: "Paczka wniosku czeka na podpis kwalifikowany.",
          });
          onCreated(response.data);
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się złożyć wniosku."),
          }),
      },
    );
  };

  const renderStep = () => {
    if (contextQuery.isLoading) return <Loading />;

    if (blocked)
      return (
        <div className={styles.previewNotice}>
          {context?.Warning ??
            "Brak zestawionej integracji UPE — dodaj ją w ustawieniach, w zakładce „Integracje”."}
        </div>
      );

    if (step === 0)
      return (
        <>
          <div className={styles.sectionTitle}>Rodzaj wniosku</div>
          {TYPES.map((option) => (
            <label key={option.value} className={styles.checkboxRow}>
              <input
                type="radio"
                checked={type === option.value}
                onChange={() => setType(option.value)}
              />
              <span>
                {option.label}
                <div className={styles.hint}>{option.hint}</div>
              </span>
            </label>
          ))}
        </>
      );

    if (step === 1)
      return (
        <>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Numer wniosku</label>
              <input
                className={styles.textInput}
                value={signature}
                autoFocus
                placeholder="np. 11/2026"
                onChange={(e) => setSignature(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Data wniosku</label>
              <input
                className={styles.textInput}
                type="date"
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Adresat</label>
            <select
              className={styles.textInput}
              value={recipientId ?? ""}
              onChange={(e) =>
                setRecipientId(e.target.value ? Number(e.target.value) : undefined)
              }
            >
              <option value="">— wybierz —</option>
              {context?.Receivers.map((r) => (
                <option key={r.Id} value={r.Id}>
                  {r.Name}
                  {r.City ? `, ${r.City}` : ""}
                </option>
              ))}
            </select>
          </div>

          {isRio && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Jednostka nadzoru (RIO)</label>
              <select
                className={styles.textInput}
                value={rioId ?? ""}
                onChange={(e) =>
                  setRioId(e.target.value ? Number(e.target.value) : undefined)
                }
              >
                <option value="">— jak adresat —</option>
                {context?.Receivers.map((r) => (
                  <option key={r.Id} value={r.Id}>
                    {r.Name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.sectionTitle}>Osoba składająca wniosek</div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Imię</label>
              <input
                className={styles.textInput}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Nazwisko</label>
              <input
                className={styles.textInput}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Stanowisko</label>
              <input
                className={styles.textInput}
                value={position}
                placeholder="np. Sekretarz Gminy"
                onChange={(e) => setPosition(e.target.value)}
              />
            </div>
          </div>

          <span className={styles.hint}>
            Nadawcą jest {context?.SenderName ?? "instytucja z konta UPE"}.
          </span>
        </>
      );

    if (step === 2)
      return (
        <>
          <div className={styles.sectionTitle}>
            Akty do przekazania ({selected.length})
          </div>

          {acts.length === 0 && (
            <div className={styles.hint}>
              Żaden akt nie czeka na przekazanie do publikacji. Przekaż akt na liście
              „Do publikacji”.
            </div>
          )}

          {acts.map((act) => (
            <label key={act.LegalActId} className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={selected.includes(act.LegalActId)}
                onChange={() => toggleAct(act.LegalActId)}
              />
              <span>
                {act.ActNumber
                  ? `${act.ActTypeName} Nr ${act.ActNumber}`
                  : act.ActTypeName}
                <div className={styles.hint}>{act.Title}</div>
              </span>
            </label>
          ))}

          <span className={styles.hint}>
            Do wniosku trafiają paczki ZIPX aktów — brakujące zostaną złożone
            automatycznie.
          </span>
        </>
      );

    return (
      <>
        <div className={styles.sectionTitle}>Podsumowanie</div>

        <div className={styles.hint}>
          Rodzaj: {TYPES.find((x) => x.value === type)?.label}
          <br />
          Numer: {signature || "—"}, data: {requestDate}
          <br />
          Nadawca: {context?.SenderName ?? "—"}
          <br />
          Adresat:{" "}
          {context?.Receivers.find((r) => r.Id === recipientId)?.Name ?? "—"}
        </div>

        <div className={styles.sectionTitle}>Akty ({selectedActs.length})</div>

        {selectedActs.map((act) => (
          <div key={act.LegalActId} className={styles.attachmentRow}>
            <span>
              {act.ActNumber
                ? `${act.ActTypeName} Nr ${act.ActNumber}`
                : act.ActTypeName}
            </span>
            <span className={styles.attachmentKind}>{act.Title}</span>
          </div>
        ))}

        <span className={styles.hint}>
          Po złożeniu wniosku zostanie założona sprawa w UPE i przygotowana paczka
          archiwalna. Podpis kwalifikowany złożysz na liście wniosków — podpisuje go
          wtyczka na Twoim stanowisku.
        </span>
      </>
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>
          Wniosek o publikację — krok {step + 1} z 4
        </div>

        <div className={styles.modalBody}>{renderStep()}</div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          {step > 0 && (
            <HollowButton text="Wstecz" onClick={() => setStep(step - 1)} />
          )}
          {step < 3 ? (
            <Button
              text="Dalej"
              disabled={!canGoNext()}
              onClick={() => setStep(step + 1)}
            />
          ) : (
            <Button
              text="Złóż wniosek"
              loading={isCreating}
              disabled={isCreating || blocked || selected.length === 0}
              onClick={submit}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicationWizard;
